import OperatorFlightPipeline from "@/components/OperatorFlightPipeline";
import { buildFlightPipeline, deriveControlAuthority, deriveSafetyView, SAFETY_TONE, operatorReadiness, stationKeepingView, homePositionView } from "@/lib/pilotageOperator";
import { num } from "@/lib/format";
import { rtlAdvisory } from "@/lib/rtlSafety";
import { ShieldAlert, Radio, Anchor, AlertOctagon, CheckCircle2 } from "lucide-react";

const TONE = { neutral: "#3B82F6", ok: "#22C55E", warn: "#F59E0B", danger: "#EF4444", unknown: "#A3A3A3" };
const Row = ({ label, value, testid, color }) => (
  <div className="flex items-center justify-between gap-2 py-0.5 border-b border-white/5">
    <span className="text-[9px] uppercase tracking-wider text-white/45">{label}</span>
    <span data-testid={testid} className="font-mono text-[10px]" style={{ color: color || "#E5E5E5" }}>{value ?? "—"}</span>
  </div>
);
const fx = (v, d = 3) => (v == null ? "—" : num(v, d));

// COCKPIT.FINAL.4 — ligne Safety : état coloré (pas de vert pour UNKNOWN) + valeur.
const SafetyRow = ({ label, it, testid }) => {
  const s = it?.state || "UNKNOWN";
  const color = SAFETY_TONE[s] || SAFETY_TONE.UNKNOWN;
  return (
    <div className="flex items-center justify-between gap-2 py-0.5 border-b border-white/5">
      <span className="text-[9px] uppercase tracking-wider text-white/45">{label}</span>
      <span data-testid={testid} className="font-mono text-[10px] flex items-center gap-1" style={{ color }} title={it?.reason || ""}>
        {it?.value != null && <span className="text-white/70">{it.value}</span>}
        <span className="font-bold">{s}</span>
      </span>
    </div>
  );
};

// Panneau opérateur (lecture seule) : synthèse, pipeline, autorité, ancrage, station keeping, échecs.
// N'ouvre aucun WS, n'appelle aucune API : callbacks fournis par le parent. Pas de state machine.
export default function OperatorCockpitPanel({ real, droneId, readyForLaunchSequence, profileLimits, altValid, telemetryFresh, drone, cockpit }) {
  const orch = real.orch;
  const status = real.status;
  // COCKPIT.FINAL.6 — consomme le modèle commun cockpitState quand fourni ; fallback = projections
  // locales (mêmes helpers) pour rester autonome hors du modèle. Aucune logique dupliquée.
  const pipeline = cockpit?.pipeline || buildFlightPipeline({ orchPhase: orch?.phase, pipeline: orch?.pipeline, reason: orch?.reason });
  // COCKPIT.FINAL.3 — projection d'autorité UNIQUE, fail-closed (UNKNOWN si preuves insuffisantes/stale).
  const authority = cockpit?.authority || deriveControlAuthority({ orch, status, telemetryFresh, conn: real?.conn });
  // COCKPIT.FINAL.4 — projection Safety UNIQUE, fail-closed (null/stale ≠ OK, failsafe dominant).
  const safety = cockpit?.safety || deriveSafetyView({ drone, status, orch, telemetryFresh });
  const ready = operatorReadiness({ conn: real.conn, canStartFlight: real.canStartFlight, altitudeValid: !!altValid });
  const sk = stationKeepingView({ sessionId: real.sessionId, status });
  const home = homePositionView(status);
  const rtl = rtlAdvisory({ home, drone });
  const observedMode = status?.px4_mode || drone?.mode;
  const pl = profileLimits;

  // Bannières dérivées de la MÊME projection d'autorité (source unique, jamais recalculée séparément).
  const pilotOverride = authority.authority === "PILOT_RC";
  const failsafe = authority.authority === "FAILSAFE";
  const terminal = pipeline.terminal;
  const phaseLabel = orch?.phase || (real.phase && real.phase !== "idle" ? `${real.phase} (liaison)` : "—");

  return (
    <div data-testid="operator-cockpit" className="bg-[#141414] border border-white/10 rounded-sm">
      <div className="px-3 py-2 flex items-center gap-2 border-b border-white/10">
        <Radio size={12} className="text-[#3B82F6]" />
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3]">Cockpit opérateur</span>
        <span data-testid="operator-ready" className="ml-auto font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border"
          style={{ color: ready.state === "ready" ? "#22C55E" : "#A3A3A3", borderColor: ready.state === "ready" ? "#22C55E66" : "#ffffff22" }}>
          {ready.state === "ready" ? "● " : ""}{ready.label}
        </span>
      </div>
      <div className="p-3 space-y-2">
        {/* 1. Alertes critiques prioritaires */}
        {pilotOverride && (
          <div data-testid="operator-pilot-override" className="rounded-sm border border-amber-500/50 bg-amber-500/10 text-amber-300 px-3 py-2 text-[10px] font-mono uppercase tracking-wider flex items-center gap-1.5">
            <ShieldAlert size={13} /> Pilote RC prioritaire — contrôle SKYCORE suspendu
          </div>
        )}
        {failsafe && (
          <div data-testid="operator-failsafe" className="rounded-sm border border-red-500/60 bg-red-500/15 text-red-300 px-3 py-2 text-[10px] font-mono uppercase tracking-wider flex items-center gap-1.5">
            <AlertOctagon size={13} /> Failsafe actif
          </div>
        )}
        {real.phase === "link_lost" && (
          <div data-testid="operator-link-lost" className="rounded-sm border border-red-500/50 bg-red-500/10 text-red-300 px-3 py-2 text-[10px] font-mono uppercase tracking-wider">Liaison de contrôle perdue</div>
        )}
        {terminal && (
          <div data-testid="operator-failure" className="rounded-sm border border-red-500/50 bg-red-500/10 text-red-300 px-3 py-2 text-[10px] font-mono">
            <div className="uppercase tracking-wider">{terminal === "OFFBOARD_NOT_ACQUIRED" ? "OFFBOARD non acquis" : terminal === "FAILED" ? "Échec séquence" : "Séquence annulée"}</div>
            {orch?.reason && <div data-testid="operator-failure-reason" className="text-red-300/80 mt-0.5">{orch.reason} <span className="text-white/40">({terminal})</span></div>}
          </div>
        )}
        {real.criticalAction && (
          <div data-testid="operator-critical" className="rounded-sm border border-amber-500/50 bg-amber-500/10 text-amber-200 px-3 py-2 text-[10px] font-mono">
            <div className="uppercase tracking-wider">{real.criticalAction.type} · {real.criticalAction.status}</div>
            {real.criticalAction.uncertain && <div data-testid="operator-critical-unknown" className="text-red-300 mt-0.5">Résultat inconnu — ne pas réessayer automatiquement</div>}
          </div>
        )}

        {/* 1b. Autorité de contrôle — projection dominante, réponse immédiate à « qui contrôle ? » */}
        <div data-testid="operator-authority-block" className="rounded-sm border px-3 py-2"
          style={{ borderColor: TONE[authority.tone] + "55", background: TONE[authority.tone] + "14" }}>
          <div className="flex items-center justify-between">
            <span className="text-[9px] uppercase tracking-widest text-white/50">Autorité</span>
            <span data-testid="operator-authority" className="font-chivo font-bold text-xs uppercase tracking-wider" style={{ color: TONE[authority.tone] }}>{authority.label}</span>
          </div>
          {authority.reason && (
            <div data-testid="operator-authority-reason" className="font-mono text-[9px] text-white/50 mt-0.5">
              {authority.reason}{authority.source ? ` · Source : ${authority.source}` : ""}
            </div>
          )}
        </div>

        {/* 1c. SAFETY — vue sécurité canonique fail-closed (COCKPIT.FINAL.4) */}
        <div data-testid="safety-section" className="rounded-sm border border-white/10 bg-white/[0.02] px-3 py-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[9px] uppercase tracking-widest text-white/50">Safety</span>
            <span data-testid="safety-global" className="font-chivo font-bold text-[11px] uppercase tracking-wider" style={{ color: SAFETY_TONE[safety.global === "FAILSAFE" ? "FAIL" : safety.global === "DEGRADED" ? "WARN" : safety.global] }}>{safety.global}</span>
          </div>
          {safety.primaryAlert && (
            <div data-testid="safety-primary-alert" className="rounded-sm px-2 py-1 mb-1.5 text-[9px] font-mono uppercase tracking-wider border"
              style={{ color: SAFETY_TONE[safety.global === "FAILSAFE" ? "FAIL" : "WARN"], borderColor: SAFETY_TONE[safety.global === "FAILSAFE" ? "FAIL" : "WARN"] + "55", background: SAFETY_TONE[safety.global === "FAILSAFE" ? "FAIL" : "WARN"] + "14" }}>
              {safety.global === "FAILSAFE" ? "PX4 FAILSAFE — " : ""}{safety.primaryAlert}
            </div>
          )}
          {safety.global === "UNKNOWN" && safety.reason && (
            <p data-testid="safety-reason" className="text-[9px] font-mono text-[#A3A3A3] mb-1.5">{safety.reason}</p>
          )}
          <div className="grid grid-cols-2 gap-x-3">
            <SafetyRow label="MAVLink" it={safety.items.mavlink} testid="safety-mavlink" />
            <SafetyRow label="RC" it={safety.items.rc} testid="safety-rc" />
            <SafetyRow label="GPS" it={safety.items.gps} testid="safety-gps" />
            <SafetyRow label="Position" it={safety.items.position} testid="safety-position" />
            <SafetyRow label="HOME" it={safety.items.home} testid="safety-home" />
            <SafetyRow label="Batterie" it={safety.items.battery} testid="safety-battery" />
            <SafetyRow label="PX4 Failsafe" it={safety.items.px4Failsafe} testid="safety-px4-failsafe" />
            <SafetyRow label="Télémétrie" it={safety.items.telemetry} testid="safety-telemetry" />
          </div>
        </div>

        {/* 2. Synthèse */}
        <div data-testid="operator-summary" className="grid grid-cols-2 gap-x-3">
          <Row label="Drone" value={droneId || status?.drone_id} testid="op-drone" />
          <Row label="SID" value={real.sessionId ? real.sessionId.slice(0, 8) : "—"} testid="op-sid" />
          <Row label="Phase" value={phaseLabel} testid="op-phase" />
          <Row label="PX4" value={status?.px4_mode} testid="op-px4" />
          <Row label="OFFBOARD" value={status?.state} testid="op-offboard" />
          <Row label="Mouvement" value={real.moveAllowed ? "autorisé" : "verrouillé"} testid="op-move" color={real.moveAllowed ? "#22C55E" : "#EF4444"} />
          <Row label="Vitesse" value={real.controlLimitsReady && pl ? `${real.speedProfile.toUpperCase()} · XY ${pl.max_xy.toFixed(2)}` : "—"} testid="op-speed" color="#3B82F6" />
        </div>

        {/* 3. Pipeline */}
        <OperatorFlightPipeline pipeline={pipeline} />

        {/* 3b. HOME PX4 (lecture seule, autoritaire Raspberry) */}
        <div data-testid="operator-home" className="grid grid-cols-2 gap-x-3 pt-1 border-t border-white/10">
          <Row label="HOME" value={home.valid ? "VALIDE" : (status?.home_position ? "INVALIDE" : "—")} testid="op-home-valid"
            color={home.valid ? "#22C55E" : (status?.home_position ? "#EF4444" : "#A3A3A3")} />
          <Row label="Âge HOME" value={home.ageS == null ? "—" : `${fx(home.ageS, 1)} s`} testid="op-home-age" />
          <Row label="HOME lat" value={home.valid ? fx(home.lat, 5) : "—"} testid="op-home-lat" />
          <Row label="HOME lon" value={home.valid ? fx(home.lon, 5) : "—"} testid="op-home-lon" />
          <Row label="HOME AMSL" value={home.altAmsl == null ? "—" : `${fx(home.altAmsl, 1)} m`} testid="op-home-amsl" />
          <Row label="HOME distance" value={rtl.geometry ? `${fx(rtl.geometry.distance, 1)} m` : "—"} testid="op-home-distance" />
          <Row label="HOME direction" value={rtl.geometry ? `${Math.round(rtl.geometry.bearing)}°${rtl.geometry.side ? ` · ${rtl.geometry.side}` : ""}` : "—"} testid="op-home-direction" />
          <Row label="Mode observé" value={observedMode || "—"} testid="op-observed-mode"
            color={/RTL/i.test(observedMode || "") ? "#F59E0B" : undefined} />
        </div>
        <div data-testid="op-rtl-advisory" className="rounded-sm border px-2 py-1.5 text-[9px] font-mono leading-snug"
          style={{ color: TONE[rtl.level === "info" ? "unknown" : rtl.level], borderColor: `${TONE[rtl.level === "info" ? "unknown" : rtl.level]}55` }}>
          RTL : {rtl.text}
        </div>

        {/* 4. Ancrage / station keeping */}
        <div data-testid="operator-station-keeping" className="rounded-sm border border-white/10 bg-white/[0.02] px-3 py-2">
          <div className="flex items-center gap-2 mb-1">
            <Anchor size={12} className="text-[#2DD4BF]" />
            <span className="text-[9px] uppercase tracking-widest text-white/50">Station keeping</span>
            {sk.live ? (
              sk.active
                ? <span data-testid="sk-state" className="ml-auto font-mono text-[9px] text-[#22C55E] flex items-center gap-1"><CheckCircle2 size={10} /> ANCRE ACTIVE</span>
                : <span data-testid="sk-state" className="ml-auto font-mono text-[9px] text-[#A3A3A3]">{sk.mode === "BODY_VELOCITY" ? "MOUVEMENT" : (sk.mode || "—")}</span>
            ) : <span data-testid="sk-waiting" className="ml-auto font-mono text-[9px] text-[#A3A3A3]">EN ATTENTE POUR CETTE SESSION</span>}
          </div>
          {sk.live && (
            <div className="grid grid-cols-2 gap-x-3">
              <Row label="Mode setpoint" value={sk.mode} testid="sk-mode" />
              <Row label="Ancre active" value={sk.active ? "OUI" : "NON"} testid="sk-active" />
              <Row label="Raison ancre" value={sk.anchorReason} testid="sk-reason" />
              <Row label="Âge ancre" value={sk.anchorAge == null ? "—" : `${fx(sk.anchorAge, 1)} s`} testid="sk-age" />
              <Row label="Erreur XY" value={sk.errXY == null ? "—" : `${fx(sk.errXY)} m`} testid="sk-errxy" />
              <Row label="Erreur Z" value={sk.errZ == null ? "—" : `${fx(sk.errZ)} m`} testid="sk-errz" />
              <Row label="Max XY" value={sk.maxXY == null ? "—" : `${fx(sk.maxXY)} m`} testid="sk-maxxy" />
              <Row label="Max Z" value={sk.maxZ == null ? "—" : `${fx(sk.maxZ)} m`} testid="sk-maxz" />
              <Row label="Cadence" value={sk.rateHz == null ? "—" : `${fx(sk.rateHz, 2)} Hz`} testid="sk-rate" />
              <Row label="Gap max" value={sk.gapMs == null ? "—" : `${fx(sk.gapMs, 0)} ms`} testid="sk-gap" />
              <Row label="Jitter" value={sk.jitterMs == null ? "—" : `${fx(sk.jitterMs, 0)} ms`} testid="sk-jitter" />
              <Row label="Segments" value={sk.segments == null ? "—" : fx(sk.segments, 0)} testid="sk-segments" />
            </div>
          )}
        </div>
        <p className="text-[9px] text-white/35 leading-relaxed">PREPARE vérifie GPS / HOME / batterie avant ARM. {readyForLaunchSequence ? "Séquence PREPARE→ARM→TAKEOFF disponible." : ""}</p>
      </div>
    </div>
  );
}
